import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import {
    DialogButtonComponent,
    DialogButtonsComponent,
    InputSearchComponent,
    RadioButtonComponent,
    RadioGroupComponent,
} from '@interacta-shared/ui';
import { NoResultComponent } from '@interacta-shared/ui-common';
import { RelatedLinkedBy } from '@modules/communities/models/related-item/related-item.model';
import { TranslatePipe } from '@ngx-translate/core';
import { concat, of } from 'rxjs';

@Component({
    selector: 'interacta-relations-dialog-community',
    templateUrl: './relations-dialog-community.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'flex flex-col max-h-352 lg:max-h-404 xl:max-h-500',
    },
    imports: [
        NgClass,
        FormsModule,
        DialogButtonComponent,
        DialogButtonsComponent,
        NoResultComponent,
        InputSearchComponent,
        RadioGroupComponent,
        RadioButtonComponent,
        TranslatePipe,
    ],
})
export class RelationsDialogCommunityComponent {
    inverseLinks = input.required<RelatedLinkedBy[]>();
    selectedItem = input<RelatedLinkedBy | null>(null);

    back = output();
    selectLink = output<{ link: RelatedLinkedBy }>();

    private readonly userSelectedLink = signal<RelatedLinkedBy | null>(null);

    readonly searchIsVisible = computed(() => this.inverseLinks().length > 10);

    private readonly initialSelectedLink = computed(() =>
        this.selectedItem()
            ? (this.inverseLinks().find(
                  (link) => link.id === this.selectedItem()?.id,
              ) ?? null)
            : null,
    );

    readonly selectedLink = computed(
        () => this.userSelectedLink() ?? this.initialSelectedLink(),
    );

    searchControl = new FormControl<string>('', { nonNullable: true });

    private readonly searchString = toSignal(
        concat(of(''), this.searchControl.valueChanges),
        { initialValue: '' },
    );

    readonly filteredInverseLinks = computed(() => {
        const search = this.searchString().toLowerCase();
        const links = this.inverseLinks();

        return search
            ? links.filter((link) => link.name.toLowerCase().includes(search))
            : links;
    });

    onSelectionChange(link: RelatedLinkedBy | null): void {
        this.userSelectedLink.set(link);
    }
}
